import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState, type DragEvent } from 'react';
import { ApiError, api, type Me } from '../api';
import { errorText, useI18n } from '../i18n';
import { Alert, PageHead, PanelShell } from '../layout';

interface Folder { id: number; name: string }
interface DriveFile { id: number; name: string; mime: string; size: number; createdAt: string }
interface Listing { folder: number | null; path: Folder[]; folders: Folder[]; files: DriveFile[]; usage: { bytes: number; quota: number } }
interface Trash { folders: (Folder & { deletedAt: string })[]; files: (DriveFile & { deletedAt: string })[]; keepDays: number }

const size = (bytes: number) => (bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
const icon = (mime: string) => (mime === 'application/pdf' ? 'PDF' : mime.startsWith('image/') ? 'IMG' : /sheet|csv/.test(mime) ? 'XLS' : /word|opendocument.text/.test(mime) ? 'DOC' : /presentation/.test(mime) ? 'PPT' : mime === 'application/zip' ? 'ZIP' : 'TXT');
const viewable = (mime: string) => mime === 'application/pdf' || /^image\/(jpeg|png|webp|gif)$/.test(mime);
const UPLOAD_ERRORS: Record<string, string> = {
  file_type_not_allowed: 'Šio tipo failų įkelti negalima.', old_office_format: 'Seno formato (.doc, .xls) failų įkelti negalima — išsaugokite kaip .docx ar .xlsx.',
  file_too_large: 'Failas per didelis (daugiausia 25 MB).', quota_exceeded: 'Baigėsi vieta failams.',
};

/** C2 — Failai: folders, uploads (also by dragging), view, rename, move, trash. */
export function FilesPage({ me, folder, onOpen }: { me: Me; folder: number | null; onOpen: (id: number | null) => void }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [showTrash, setShowTrash] = useState(false);
  const [drag, setDrag] = useState(false);
  const [uploads, setUploads] = useState<{ name: string; state: 'wait' | 'done' | string }[]>([]);
  const [moving, setMoving] = useState<{ type: 'folder' | 'file'; id: number; name: string } | null>(null);
  const listing = useQuery({ queryKey: ['drive', folder], queryFn: () => api.get<Listing>(`api/v1/drive${folder ? `?folder=${folder}` : ''}`), retry: false });
  const trash = useQuery({ queryKey: ['drive-trash'], enabled: showTrash, queryFn: () => api.get<Trash>('api/v1/drive/trash') });
  const refresh = () => Promise.all([queryClient.invalidateQueries({ queryKey: ['drive'] }), queryClient.invalidateQueries({ queryKey: ['drive-trash'] })]);
  const act = useMutation({ mutationFn: (fn: () => Promise<unknown>) => fn(), onSuccess: refresh });

  const upload = async (files: FileList | File[]) => {
    const list = [...files];
    setUploads(list.map((f) => ({ name: f.name, state: 'wait' })));
    for (const [n, file] of list.entries()) {
      const body = new FormData();
      if (folder) body.append('folderId', String(folder)); // before the file: the server reads fields that come first
      body.append('file', file);
      const r = await fetch('api/v1/drive/files', { method: 'POST', body, credentials: 'same-origin', headers: { 'x-fsp-request': '1' } });
      const code = r.ok ? 'done' : (((await r.json().catch(() => null)) as { error?: { code?: string } } | null)?.error?.code ?? 'server_error');
      setUploads((u) => u.map((x, i) => (i === n ? { ...x, state: code } : x)));
    }
    await refresh();
  };
  const onDrop = (e: DragEvent) => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files.length) void upload(e.dataTransfer.files); };
  const rename = (type: 'folder' | 'file', id: number, current: string) => {
    const name = prompt(t('Naujas pavadinimas'), current)?.trim();
    if (name && name !== current) act.mutate(() => api.patch(`api/v1/drive/${type}s/${id}`, { name }));
  };
  const remove = (type: 'folder' | 'file', id: number) => act.mutate(() => api.delete(`api/v1/drive/${type}s/${id}`));

  const l = listing.data;
  // A folder that no longer exists (or went to the trash): back to the top.
  const missing = listing.isError && listing.error instanceof ApiError && listing.error.status === 404;
  useEffect(() => { if (missing) onOpen(null); }, [missing]); // eslint-disable-line react-hooks/exhaustive-deps
  const used = l ? Math.min(100, Math.round((l.usage.bytes / l.usage.quota) * 100)) : 0;
  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Dokumentai')} title={t('Failai')} text={t('Įmonės dokumentai aplankuose. Ištrinti failai 30 dienų laikomi šiukšliadėžėje.')} />}>
      {act.isError ? <Alert kind="error">{errorText(t, act.error)}</Alert> : null}
      <section className={`panel-card drive${drag ? ' is-dragging' : ''}`} onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={onDrop}>
        <div className="drive-bar">
          <nav className="drive-path" aria-label={t('Aplankas')}>
            <button type="button" className="text-link" onClick={() => { setShowTrash(false); onOpen(null); }}>{t('Failai')}</button>
            {showTrash ? <><span>›</span><strong>{t('Šiukšliadėžė')}</strong></> : l?.path.map((p) => <span key={p.id}><span>›</span> <button type="button" className="text-link" onClick={() => onOpen(p.id)}>{p.name}</button></span>)}
          </nav>
          {!showTrash ? <>
            <button type="button" className="button button--ghost button--small" onClick={() => { const name = prompt(t('Aplanko pavadinimas'))?.trim(); if (name) act.mutate(() => api.post('api/v1/drive/folders', { name, parentId: folder })); }}>+ {t('Naujas aplankas')}</button>
            <button type="button" className="button button--primary button--small" onClick={() => input.current?.click()}>{t('Įkelti failus')}</button>
            <input ref={input} type="file" multiple hidden onChange={(e) => { if (e.target.files?.length) void upload(e.target.files); e.target.value = ''; }} />
          </> : null}
          <button type="button" className="button button--ghost button--small" onClick={() => setShowTrash(!showTrash)}>{showTrash ? t('Atgal į failus') : t('Šiukšliadėžė')}</button>
        </div>
        {uploads.length ? (
          <ul className="drive-uploads">{uploads.map((u, i) => (
            <li key={i} className={u.state === 'done' ? 'is-done' : u.state === 'wait' ? '' : 'is-error'}>{u.name} — {u.state === 'wait' ? t('Įkeliama…') : u.state === 'done' ? t('Įkelta') : t(UPLOAD_ERRORS[u.state] ?? 'Nepavyko įkelti.')}</li>
          ))}</ul>
        ) : null}

        {!showTrash && l ? (
          <div className="drive-list">
            {!l.folders.length && !l.files.length ? <div className="empty-state compact"><h3>{t('Aplankas tuščias')}</h3><p>{t('Įkelkite failus arba nutempkite juos čia.')}</p></div> : null}
            {l.folders.map((f) => (
              <div key={`d${f.id}`} className="drive-row">
                <button type="button" className="drive-open" onClick={() => onOpen(f.id)}><span className="drive-icon drive-icon--folder" aria-hidden="true" /> <strong>{f.name}</strong></button>
                <span className="drive-actions">
                  <button type="button" className="text-link" onClick={() => rename('folder', f.id, f.name)}>{t('Pervadinti')}</button>
                  <button type="button" className="text-link" onClick={() => setMoving({ type: 'folder', id: f.id, name: f.name })}>{t('Perkelti')}</button>
                  <button type="button" className="delete-x" aria-label={t('Ištrinti')} title={t('Ištrinti')} onClick={() => remove('folder', f.id)}>×</button>
                </span>
              </div>
            ))}
            {l.files.map((f) => (
              <div key={`f${f.id}`} className="drive-row">
                {viewable(f.mime)
                  ? <Link className="drive-open" to="/view" search={{ type: 'drive', id: f.id, title: f.name }}><span className="drive-icon">{icon(f.mime)}</span> {f.name}</Link>
                  : <a className="drive-open" href={`api/v1/drive/files/${f.id}/content?download=1`}><span className="drive-icon">{icon(f.mime)}</span> {f.name}</a>}
                <small>{size(f.size)} · {new Date(f.createdAt).toLocaleDateString()}</small>
                <span className="drive-actions">
                  <a className="text-link" href={`api/v1/drive/files/${f.id}/content?download=1`}>{t('Atsisiųsti')}</a>
                  <button type="button" className="text-link" onClick={() => rename('file', f.id, f.name)}>{t('Pervadinti')}</button>
                  <button type="button" className="text-link" onClick={() => setMoving({ type: 'file', id: f.id, name: f.name })}>{t('Perkelti')}</button>
                  <button type="button" className="delete-x" aria-label={t('Ištrinti')} title={t('Ištrinti')} onClick={() => remove('file', f.id)}>×</button>
                </span>
              </div>
            ))}
          </div>
        ) : null}

        {showTrash && trash.data ? (
          <div className="drive-list">
            <p className="muted">{t('Elementai ištrinami visam laikui po {n} dienų.', { n: trash.data.keepDays })}</p>
            {!trash.data.folders.length && !trash.data.files.length ? <div className="empty-state compact"><h3>{t('Šiukšliadėžė tuščia')}</h3></div> : null}
            {[...trash.data.folders.map((f) => ({ ...f, type: 'folder' as const, mime: '' })), ...trash.data.files.map((f) => ({ ...f, type: 'file' as const }))].map((f) => (
              <div key={`${f.type}${f.id}`} className="drive-row">
                <span className="drive-open">{f.type === 'folder' ? <span className="drive-icon drive-icon--folder" aria-hidden="true" /> : <span className="drive-icon">{icon(f.mime)}</span>} {f.name}</span>
                <small>{t('Ištrinta')} {new Date(f.deletedAt).toLocaleDateString()}</small>
                <span className="drive-actions">
                  <button type="button" className="text-link" onClick={() => act.mutate(() => api.post('api/v1/drive/trash/restore', { type: f.type, id: f.id }))}>{t('Atkurti')}</button>
                  <button type="button" className="text-danger" onClick={() => { if (confirm(t('Ištrinti „{name}“ visam laikui?', { name: f.name }))) act.mutate(() => api.delete(`api/v1/drive/trash/${f.type}/${f.id}`)); }}>{t('Ištrinti visam laikui')}</button>
                </span>
              </div>
            ))}
            {trash.data.folders.length || trash.data.files.length ? (
              <div className="form-actions"><button type="button" className="button button--danger button--small" onClick={() => { if (confirm(t('Išvalyti šiukšliadėžę? Failų atkurti nebus galima.'))) act.mutate(() => api.delete('api/v1/drive/trash')); }}>{t('Išvalyti šiukšliadėžę')}</button></div>
            ) : null}
          </div>
        ) : null}

        {l ? <div className="drive-usage"><span style={{ width: `${used}%` }} /><small>{t('Užimta {used} iš {quota}', { used: size(l.usage.bytes), quota: size(l.usage.quota) })}</small></div> : null}
      </section>
      <MoveDialog item={moving} onClose={() => setMoving(null)} onMove={(target) => {
        const m = moving!;
        setMoving(null);
        act.mutate(() => api.patch(`api/v1/drive/${m.type}s/${m.id}`, m.type === 'folder' ? { parentId: target } : { folderId: target }));
      }} />
    </PanelShell>
  );
}

function MoveDialog({ item, onClose, onMove }: { item: { type: 'folder' | 'file'; id: number; name: string } | null; onClose: () => void; onMove: (target: number | null) => void }) {
  const { t } = useI18n();
  const folders = useQuery({ queryKey: ['drive-folders'], enabled: Boolean(item), queryFn: () => api.get<{ folders: { id: number; path: string }[] }>('api/v1/drive/folders').then((r) => r.folders) });
  const [target, setTarget] = useState('');
  if (!item) return null;
  return (
    <div className="drive-move" role="dialog" aria-modal="true">
      <div className="dialog-card">
        <p className="eyebrow">{t('Perkelti')}</p><h2>{item.name}</h2>
        <label>{t('Į aplanką')}<select value={target} onChange={(e) => setTarget(e.target.value)}>
          <option value="">{t('Failai')} ({t('pagrindinis')})</option>
          {folders.data?.filter((f) => !(item.type === 'folder' && f.id === item.id)).map((f) => <option key={f.id} value={f.id}>{f.path}</option>)}
        </select></label>
        <div className="dialog-actions">
          <button type="button" className="button button--ghost" onClick={onClose}>{t('Atšaukti')}</button>
          <button type="button" className="button button--primary" onClick={() => onMove(target ? Number(target) : null)}>{t('Perkelti')}</button>
        </div>
      </div>
    </div>
  );
}
